import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const id = useMemo(() => searchParams.get('id') || '', [searchParams]);
  // Backward-compat: older reset emails included `email=` instead of `id=`.
  const legacyEmail = useMemo(
    () => searchParams.get('email') || '',
    [searchParams]
  );

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const missingParams = !token || (!id && !legacyEmail);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const payload = { token, password };
      if (id) payload.id = id;
      else if (legacyEmail) payload.email = legacyEmail;

      const response = await api.post('/auth/reset-password', payload);
      const { token: authToken, user } = response.data;
      if (authToken) {
        localStorage.setItem('token', authToken);
        if (user?.shop) {
          localStorage.setItem(`runa:autoPassword:${user.shop}`, password);
        }
        if (typeof setUser === 'function') setUser(user);
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex flex-col items-center text-center">
          <img
            src="https://cdn.prod.website-files.com/6598727d8180e7b6c126a6cf/69344ca971a7628e577f32ab_runa_logo.png"
            alt="Runa"
            className="w-16 h-16 rounded-full mb-4"
          />
          <h1 className="text-2xl font-light tracking-tight">RUNA</h1>
        </div>

        <div className="mb-8 text-center">
          <h2 className="text-2xl font-semibold text-neutral-900">
            Choose a new password
          </h2>
        </div>

        {missingParams ? (
          <div className="p-4 border border-red-200 bg-red-50 text-red-700 text-sm">
            This reset link is invalid. Please request a new one from the{' '}
            <Link to="/forgot-password" className="link">
              forgot password
            </Link>{' '}
            page.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 border border-red-200 bg-red-50 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="label">New password</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="label">Confirm password</label>
              <input
                type="password"
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                placeholder="Repeat your password"
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="spinner mr-2"></span>
                  Updating
                </span>
              ) : (
                'Update password'
              )}
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-sm text-neutral-500">
          <Link to="/login" className="link">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
